"use client";

// ЕДИНЫЕ поля ввода портала.
//
// ЗАЧЕМ. Аудит 08.08 нашёл 13 отдельных объявлений `const inputCls` — у каждого
// модуля свой инпут: разный кегль (12 / 13 / 13.5px), разный радиус, разное
// поведение фокуса. Поле ввода — самый частый элемент форм, и именно на нём
// разнобой заметнее всего, когда две вкладки открыты рядом.
//
// ⚠️ Даты — НЕ через <Input type="date">, а через
// `@/components/ui/date-picker` (красивый календарь портала). Нативный
// date-инпут выглядит по-разному в каждом браузере и выбивается из оформления.

import { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from "react";

/** Класс поля ввода — для мест, где нужен голый <input> (маски, сторонние обёртки). */
export const INPUT_CLS =
  // Форма — из темы (--radius-md), как у кнопок и панелей. Раньше здесь была
  // пилюля: при общем радиусе 18 она смотрелась уместно, но на 8 круглое поле
  // внутри прямоугольной панели выпадает из окружения (Максим, 26.08.2026).
  "w-full rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-[12px] text-[var(--text)] outline-none transition placeholder:text-[var(--textMuted)] focus:border-[var(--primary)] disabled:opacity-60";

/**
 * Класс МНОГОСТРОЧНОГО поля — для голых <textarea> вне компонента `Textarea`.
 *
 * ⚠️ ЗАЧЕМ ОТДЕЛЬНАЯ КОНСТАНТА. `INPUT_CLS` — пилюля (`rounded-full`), и на
 * многострочном поле она превращает рамку в капсулу: у высокой коробки радиус
 * вырастает до половины высоты, боковые закругления съезжают на текст и первая
 * буква строки упирается в дугу. Ровно это и было видно в карточке задачи IT
 * («Описание» на несколько строк). Радиус тут не переопределяется поверх
 * `rounded-full`, а ЗАМЕНЯЕТ его: две конкурирующие tailwind-утилиты имеют
 * одинаковый вес, и кто победит — зависит от порядка в собранном CSS, а не от
 * порядка в className.
 *
 * Где что: поле в одну строку (в т.ч. строка чата, которая лишь изредка
 * подрастает и стоит в ряду круглых кнопок) — `INPUT_CLS`; поле, рассчитанное
 * на абзац (`rows > 1`, авторост, `resize-y`) — `TEXTAREA_CLS`.
 */
// Однострочное и многострочное поле теперь одной формы, но константа
// остаётся: на неё ссылаются готовые вызовы, и она задаёт СМЫСЛ — «поле
// на абзац». Разойдутся формы снова — менять здесь, а не по местам.
export const TEXTAREA_CLS = INPUT_CLS;

/** Состояние проверки поля (№43 каталога движения). */
export type FieldState = "ok" | "bad" | undefined;

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { state?: FieldState }
>(function Input({ className, state, ...rest }, ref) {
  // ── Инлайн-валидация (№43) ────────────────────────────────────────────────
  // Ошибка не ждёт отправки формы: поле краснеет и коротко вздрагивает прямо
  // при вводе, верный формат подтверждается зелёной рамкой.
  //
  // ⚠️ Тряска запускается ТОЛЬКО в момент перехода в «плохо», а не на каждый
  // символ: иначе поле трясётся всё время, пока значение неверно, и это
  // превращается в тик. Ключ — предыдущее состояние.
  const prev = useRef<FieldState>(undefined);
  const [shake, setShake] = useState(false);
  useEffect(() => {
    const before = prev.current;
    prev.current = state;
    if (state === "bad" && before !== "bad") {
      setShake(true);
      const t = setTimeout(() => setShake(false), 400);
      return () => clearTimeout(t);
    }
  }, [state]);

  return (
    <input
      {...rest}
      ref={ref}
      aria-invalid={state === "bad" || undefined}
      className={`${INPUT_CLS} ${state === "ok" ? "field-ok" : state === "bad" ? "field-bad" : ""} ${
        shake ? "field-shake" : ""
      } ${className ?? ""}`}
    />
  );
});

/**
 * Многострочное поле, которое РАСТЁТ ПОД ТЕКСТ.
 *
 * Раньше высота была фиксированной (min-h-[64px]), и абзац из пяти строк
 * прокручивался внутри коробки в четыре — при том, что вокруг было пусто.
 * Теперь поле подгоняется под содержимое при каждом изменении значения и при
 * первом показе (важно для форм, которые открываются уже заполненными).
 *
 * «В разумных пределах»: снизу — те же 64px, сверху — `max-h`, после которого
 * появляется прокрутка (иначе одно поле с длинным текстом вытолкнет из окна
 * всё остальное). Предел задаётся классом и переопределяется через className.
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
  function Textarea({ className, value, ...rest }, ref) {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    const fit = useCallback((el: HTMLTextAreaElement | null) => {
      if (!el) return;
      // Сначала сбрасываем высоту: иначе scrollHeight не даст полю уменьшиться,
      // когда текст стал короче.
      el.style.height = "auto";
      // scrollHeight — это содержимое с внутренними отступами, но БЕЗ рамок, а
      // высота поля считается по border-box. Без добавки в пару пикселей поле
      // оставалось ниже нужного и показывало полосу прокрутки на ровном месте —
      // ровно то, из-за чего абзац в четыре строки выглядел обрезанным.
      const borders = el.offsetHeight - el.clientHeight;
      el.style.height = `${el.scrollHeight + borders}px`;
    }, []);

    // Значение приходит снаружи (контролируемое поле) — пересчитываем на каждое.
    useLayoutEffect(() => { fit(innerRef.current); }, [value, fit]);

    return (
      <textarea
        {...rest}
        value={value}
        ref={(el) => {
          innerRef.current = el;
          fit(el);
          if (typeof ref === "function") ref(el);
          else if (ref) ref.current = el;
        }}
        onInput={(e) => {
          fit(e.currentTarget);
          rest.onInput?.(e);
        }}
        className={`${INPUT_CLS} field-area min-h-[64px] max-h-[320px] resize-y ${className ?? ""}`}
      />
    );
  },
);

/**
 * Подпись + поле + пояснение. Обёрнуто в <label>, поэтому клик по подписи
 * ставит фокус в поле — это и доступность, и просто удобнее попадать.
 */
export function Field({
  label,
  children,
  hint,
  required,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  /** Пояснение под полем: формат, единица измерения, откуда берётся значение. */
  hint?: ReactNode;
  required?: boolean;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 ${className ?? ""}`}>
      <span className="field-label">
        {label}
        {required && <span className="ml-0.5 text-[var(--danger)]">*</span>}
      </span>
      {children}
      {hint && <span className="text-[11px] text-[var(--textMuted)]">{hint}</span>}
    </label>
  );
}
